import {
  getCatalogsWithItemsForTree,
  getServiceCatalogItemsByFolderId,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ServiceCatalog/ServiceCatalogService';
import { getSubFolders } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { ImageInfo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Image/ImageInfo';
import { getPortalSetup } from 'utils/PortalSetup';
import { findConfigurationsInFolder } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/ConfigurationStorageService';
import { ElementType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/ElementType';
import { defaultActionHandler, isDefaultGridAction } from 'utils/ActionUtils';
import { ActionHeader, EntityActionType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { getActionHeadersForAnyEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/Common/CommonActionService';
import {
  useSettingsStore,
  getRootRootWorkflowCatalogItems as getRootRootWorkflowCatalogItemsSetting,
} from 'domains/portal/SettingsStore';
import { WorkflowNodeIcon } from './WorkflowNodeIcon';

export interface IWorkflowCatalogNode {
  id: string;
  serviceCatalogId: string;
  entityDescription?: string;
  entityName: string;
  /** should be ID of containing folder */
  entityFolderId?: string;
  icon?: ImageInfo;
  catalogType?: string;
  nodeType: ServiceCatalogTreeNodeType;
  isReportFolderTreeItem?: boolean;
}

const ELEMENT_REGISTRATION_CATALOG_TYPE =
  'DecisionsFramework.ServiceLayer.Services.ConfigurationStorage.ElementRegistration';

export const serviceCatalogEventId = 'ServiceCatalogEvent';

export enum ServiceCatalogTreeNodeType {
  Folder,
  ServiceCatalog,
}

export async function loadWorkflowCatalogNodes(
  parent: IWorkflowCatalogNode
): Promise<ITreeNode<IWorkflowCatalogNode>[]> {
  let subItems: IWorkflowCatalogNode[];
  if (parent.isReportFolderTreeItem) {
    subItems = await getReportCatalogItems(parent.id, parent.serviceCatalogId);
  } else {
    subItems = await getSubWorkflowCatalogItems(parent.id);
  }
  return subItems.map(folderNode => createWorkflowCatalogNode(folderNode));
}

export async function loadTree() {
  const rootFolders = await getRootWorkflowCatalogItems();
  const rootNodes = rootFolders.map(folderNode => createWorkflowCatalogNode(folderNode));

  return rootNodes;
}

export async function getRootWorkflowCatalogItems(): Promise<IWorkflowCatalogNode[]> {
  const rootFolderTreeItems = getRootItems();
  const rootWorkflowCatalogItems = getRootRootWorkflowCatalogItemsSetting(useSettingsStore.getState());
  let additionalItems;
  if (rootWorkflowCatalogItems) {
    additionalItems = getSubWorkflowCatalogItems(rootWorkflowCatalogItems);
  }

  const results = await Promise.all([additionalItems ?? [], rootFolderTreeItems]);
  return [...results.flatMap(item => item)];
}

async function getRootItems() {
  let rootItems = await getCatalogsWithItemsForTree();

  const rootFolderTreeItems = rootItems
    .map(result => {
      return {
        id: result.FolderID,
        serviceCatalogId: result.FolderID,
        entityName: result.FolderName,
        icon: result.Icon,
        nodeType: ServiceCatalogTreeNodeType.Folder,
      } as IWorkflowCatalogNode;
    })
    .filter(f => f.id !== getPortalSetup()?.Settings?.RootWorkflowCatalogItems);

  return rootFolderTreeItems;
}

async function getSubWorkflowCatalogItems(folderId: string): Promise<IWorkflowCatalogNode[]> {
  const subfolders = getSubFoldersItems(folderId);
  const subItems = getSubItems(folderId);

  const results = await Promise.all([subItems, subfolders]);

  return [...results.flatMap(item => item)];
}

async function getReportCatalogItems(
  reportFolderId: string,
  serviceCatalogId: string
): Promise<IWorkflowCatalogNode[]> {
  const reports = await findConfigurationsInFolder({ folderID: reportFolderId, attributes: [ElementType.Report] });

  return reports.map(result => {
    return {
      id: result.ComponentRegistrationId,
      entityName: result.EntityName,
      catalogType: ELEMENT_REGISTRATION_CATALOG_TYPE,
      entityDescription: result.EntityDescription,
      isReportFolderTreeItem: true,
      nodeType: ServiceCatalogTreeNodeType.ServiceCatalog,
      entityFolderId: serviceCatalogId, // it's not really the entityFolderId, but it's the parent item we need for navigation
      // serviceCatalogId,
    } as IWorkflowCatalogNode;
  });
}

async function getSubFoldersItems(folderId: string): Promise<IWorkflowCatalogNode[]> {
  const result = await getSubFolders({ id: folderId });

  return result.map(result => {
    return {
      id: result.FolderID,
      serviceCatalogId: result.FolderID,
      entityFolderId: folderId, // needed to navigate up from subfolders programmatically in mobile
      entityName: result.EntityName,
      nodeType: ServiceCatalogTreeNodeType.Folder,
    } as IWorkflowCatalogNode;
  });
}

async function getSubItems(folderId: string) {
  const results = await getServiceCatalogItemsByFolderId({ folderID: folderId });

  return results.map(result => {
    const isReportFolder = result.ReportFolderId?.length;
    return {
      id: isReportFolder ? result.ReportFolderId : result.ServiceCatalogId,
      entityFolderId: folderId,
      serviceCatalogId: result.ServiceCatalogId,
      entityName: result.EntityName,
      icon: result.ImageInfo,
      catalogType: isReportFolder
        ? undefined
        : 'DecisionsFramework.ServiceLayer.Services.ServiceCatalog.ServiceCatalogItem',
      entityDescription: isReportFolder ? undefined : result.EntityDescription,
      nodeType: isReportFolder ? ServiceCatalogTreeNodeType.Folder : ServiceCatalogTreeNodeType.ServiceCatalog,
      isReportFolderTreeItem: !!isReportFolder,
    } as IWorkflowCatalogNode;
  });
}

export async function getDefaultCatalogAction(catalogItem: IWorkflowCatalogNode) {
  const actionsHeaders = await getActionHeadersForAnyEntity({
    actionTypes: [EntityActionType.Silverlight, EntityActionType.WebForm],
    typeName: catalogItem.catalogType,
    entityID: catalogItem.id,
  });

  let defaultAction: ActionHeader | undefined;
  for (let action of actionsHeaders) {
    if (catalogItem.isReportFolderTreeItem) {
      if (action?.Nm === 'Run Report') {
        defaultAction = action;
      }
    } else {
      if (isDefaultGridAction(action)) {
        defaultAction = action;
        break;
      }

      if (action.Nm === 'Open URL' || action.Nm.indexOf('Navigate To [') > -1 || action.Nm === catalogItem.entityName) {
        defaultAction = action;
      }
    }
  }

  return defaultAction;
}

export async function runWorkflowCatalogAction(catalogItem: IWorkflowCatalogNode) {
  const defaultAction = await getDefaultCatalogAction(catalogItem);
  if (defaultAction) {
    await defaultActionHandler(defaultAction, catalogItem.id);
  }
}

// export function getDefaultCatalogAction

export function createWorkflowCatalogNode(catalogItem: IWorkflowCatalogNode): ITreeNode<IWorkflowCatalogNode> {
  // track these by server side ID, not client-side GUID, so they are navigable by URL
  // most workflows will have a serviceCatalogId, but report type ones only have an id.
  // Also, for subfolder ones, the .id will be it's parent folder (confusingly, a mistake?)
  // this condition handles all those cases:
  const nodeId =
    catalogItem.catalogType === ELEMENT_REGISTRATION_CATALOG_TYPE
      ? catalogItem.id
      : catalogItem.serviceCatalogId ?? catalogItem.id;

  return {
    nodeId,
    name: catalogItem.entityName,
    data: catalogItem,
    isActive: false,
    isSelectable: true,
    isExpanded: false,
    hasChildren: catalogItem.nodeType === ServiceCatalogTreeNodeType.Folder,
    icon: <WorkflowNodeIcon {...catalogItem} />,
  } as ITreeNode<IWorkflowCatalogNode>;
}

export function isWorkflowFolderNode(node: IWorkflowCatalogNode) {
  return node.nodeType === ServiceCatalogTreeNodeType.Folder;
}
